import { useState, useRef, useEffect, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveVideo } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function VideoSection({ block, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const [showModal, setShowModal] = useState(false);
    const video = resolveVideo(content, 'video', mediaFolders) || resolveVideo(content, '', mediaFolders);
    const accent = config.accent_color || primaryColor;
    if (!video) {
        // Placeholder when no video URL
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle}>
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                    <div className="w-full aspect-video rounded-2xl bg-gray-900 border-2 border-dashed border-gray-700 flex flex-col items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-3">
                            <svg className="w-8 h-8 text-white/50 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                        <p className="text-sm text-white/40 font-medium">Agrega una URL de video</p>
                        <p className="text-xs text-white/25 mt-1">YouTube o Vimeo</p>
                    </div>
                </div>
            </section>
        );
    }

    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';
    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    // Resolve custom thumbnail
    const customThumb = (() => {
        if (content.thumb_folder_id && content.thumb_file_id && mediaFolders[content.thumb_folder_id]) {
            const f = mediaFolders[content.thumb_folder_id].find(x => String(x.id) === String(content.thumb_file_id));
            if (f) return f.image_url;
        }
        return content.thumb_url || null;
    })();
    const thumbnail = customThumb || video.thumb;

    // Thumbnail + play button component (reusable)
    const ThumbnailPlay = ({ className = '', aspect = 'aspect-video' }) => {
        // File-based video without custom thumbnail: render native video player directly
        if (video.type === 'file' && !customThumb) {
            return (
                <div className={`relative rounded-2xl overflow-hidden shadow-xl ${aspect} bg-gray-900 ${className}`}>
                    <video src={video.src} className="w-full h-full object-cover" controls preload="metadata" />
                </div>
            );
        }
        return (
            <div className={`relative rounded-2xl overflow-hidden shadow-xl ${aspect} bg-gray-900 cursor-pointer group ${className}`}
                onClick={() => setShowModal(true)}>
                {thumbnail && <img src={thumbnail} alt={content.title || 'Video'} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl"
                        style={{ backgroundColor: accent, boxShadow: `0 0 0 8px ${hexToRgba(accent, 0.15)}` }}>
                        <svg className="w-7 h-7 md:w-8 md:h-8 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                </div>
            </div>
        );
    };

    // Video modal
    const VideoModal = () => showModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8"
            onClick={() => setShowModal(false)}>
            <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
                <button onClick={() => setShowModal(false)}
                    className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                    {video.type === 'file' ? (
                        <video src={video.src} className="absolute inset-0 w-full h-full" controls autoPlay />
                    ) : (
                        <iframe src={`${video.embed}?autoplay=1&rel=0&modestbranding=1`}
                            className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    )}
                </div>
            </div>
        </div>
    ) : null;

    // ── With text ──
    if (variant === 'with-text') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <ThumbnailPlay />
                        <div>
                            {content.tag && <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: s.accent }}>{content.tag}</span>}
                            {content.title && <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4" style={{ color: s.text }}>{content.title}</h2>}
                            {content.subtitle && <p className="text-base leading-relaxed" style={{ color: s.textSub }}>{content.subtitle}</p>}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <VideoModal />
            </section>
        );
    }

    // ── Fullwidth ──
    if (variant === 'fullwidth') {
        return (
            <section className="relative" style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#000000', ...s.bgStyle }}>
                <div ref={ref} className={anim}>
                    <ThumbnailPlay className="rounded-none" aspect="aspect-video" />
                </div>
                <VideoModal />
            </section>
        );
    }

    // ── Background ──
    if (variant === 'background') {
        return (
            <section className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
                {thumbnail && <img src={thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
                <div className="absolute inset-0 bg-gray-900/70" />
                <div ref={ref} className={`relative z-10 text-center text-white px-4 max-w-3xl mx-auto py-20 ${anim}`}>
                    {content.tag && <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: accent }}>{content.tag}</span>}
                    {content.title && <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4">{content.title}</h2>}
                    {content.subtitle && <p className="text-lg text-white/70 mb-8">{content.subtitle}</p>}
                    <button onClick={() => setShowModal(true)}
                        className="inline-flex items-center gap-2 px-8 py-3.5 bg-white rounded-full font-semibold text-sm shadow-lg hover:scale-105 transition-all cursor-pointer" style={{ color: accent }}>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        {content.play_text || 'Ver video'}
                    </button>
                </div>
                <VideoModal />
            </section>
        );
    }

    // ── Cinema ──
    if (variant === 'cinema') {
        return (
            <section className="relative py-16 md:py-24 overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {(content.tag || content.title) && (
                        <div className="text-center mb-10">
                            {content.tag && <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: accent }}>{content.tag}</span>}
                            {content.title && <h2 className="text-3xl md:text-4xl font-bold mt-2 text-white">{content.title}</h2>}
                            {content.subtitle && <p className="text-base text-white/50 mt-3 max-w-2xl mx-auto">{content.subtitle}</p>}
                        </div>
                    )}
                    <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.6)] cursor-pointer group"
                        style={{ aspectRatio: '21/9' }}
                        onClick={() => setShowModal(true)}>
                        {thumbnail && <img src={thumbnail} alt={content.title || 'Video'} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/30 group-hover:from-black/70 transition-colors duration-500" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center text-white transition-all duration-500 group-hover:scale-110 backdrop-blur-sm"
                                style={{ backgroundColor: hexToRgba(accent, 0.85), boxShadow: `0 0 0 12px ${hexToRgba(accent, 0.12)}, 0 8px 40px ${hexToRgba(accent, 0.3)}` }}>
                                <svg className="w-9 h-9 md:w-10 md:h-10 ml-1.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                        </div>
                        {/* Cinema bars decoration */}
                        <div className="absolute top-0 left-0 right-0 h-4 bg-black/80" />
                        <div className="absolute bottom-0 left-0 right-0 h-4 bg-black/80" />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <VideoModal />
            </section>
        );
    }

    // ── Playlist ──
    if (variant === 'playlist') {
        const playlistItems = (content.items && content.items.length > 0) ? content.items : [{ title: content.title || '', video_url: video.src || content.video_url || content.url, thumb_url: thumbnail }];
        const [activeIdx, setActiveIdx] = useState(null);
        const activeItem = activeIdx !== null ? playlistItems[activeIdx] : null;
        const resolvePlaylistVideo = (item) => {
            const url = item.video_url || item.url || '';
            const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
            if (yt) return { type: 'youtube', id: yt[1], embed: `https://www.youtube-nocookie.com/embed/${yt[1]}`, thumb: item.thumb_url || `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
            const vim = url.match(/vimeo\.com\/(\d+)/);
            if (vim) return { type: 'vimeo', id: vim[1], embed: `https://player.vimeo.com/video/${vim[1]}`, thumb: item.thumb_url || null };
            if (url) return { type: 'file', src: url, thumb: item.thumb_url || null };
            return null;
        };
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`mx-auto px-4 sm:px-6 lg:px-8 ${s.maxWidth} ${anim}`}>
                    {(content.tag || content.title) && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {playlistItems.map((item, i) => {
                            const pv = resolvePlaylistVideo(item);
                            const pThumb = pv?.thumb || item.thumb_url;
                            return (
                                <div key={i} className="rounded-xl overflow-hidden shadow-md cursor-pointer group transition-all duration-300 hover:shadow-xl"
                                    style={{ backgroundColor: s.dark ? '#1f2937' : '#f9fafb' }}
                                    onClick={() => setActiveIdx(i)}>
                                    <div className="relative aspect-video bg-gray-900">
                                        {pThumb && <img src={pThumb} alt={item.title || `Video ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                                        {!pThumb && <div className="w-full h-full flex items-center justify-center bg-gray-800"><svg className="w-10 h-10 text-gray-500" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>}
                                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110"
                                                style={{ backgroundColor: hexToRgba(accent, 0.85), boxShadow: `0 0 0 6px ${hexToRgba(accent, 0.15)}` }}>
                                                <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                            </div>
                                        </div>
                                    </div>
                                    {item.title && <div className="px-3 py-2.5"><p className="text-sm font-medium truncate" style={{ color: s.text }}>{item.title}</p></div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
                {activeIdx !== null && (() => {
                    const pv = resolvePlaylistVideo(activeItem);
                    return (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8"
                            onClick={() => setActiveIdx(null)}>
                            <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
                                <button onClick={() => setActiveIdx(null)}
                                    className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                                    {pv?.type === 'file' ? (
                                        <video src={pv.src} className="absolute inset-0 w-full h-full" controls autoPlay />
                                    ) : pv?.embed ? (
                                        <iframe src={`${pv.embed}?autoplay=1&rel=0&modestbranding=1`}
                                            className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </section>
        );
    }

    // ── Autoplay Scroll ──
    if (variant === 'autoplay-scroll') {
        const videoRef = useRef(null);
        const containerRef = useRef(null);
        const [isMuted, setIsMuted] = useState(true);
        const [isPlaying, setIsPlaying] = useState(false);
        const [showThumb, setShowThumb] = useState(video.type !== 'file');
        const isFile = video.type === 'file';

        useEffect(() => {
            if (!isFile || !containerRef.current) return;
            const el = containerRef.current;
            const obs = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    videoRef.current?.play().catch(() => {});
                    setIsPlaying(true);
                } else {
                    videoRef.current?.pause();
                    setIsPlaying(false);
                }
            }, { threshold: 0.3 });
            obs.observe(el);
            return () => obs.disconnect();
        }, [isFile]);

        const toggleMute = useCallback(() => {
            if (videoRef.current) {
                videoRef.current.muted = !videoRef.current.muted;
                setIsMuted(!videoRef.current.muted);
            }
        }, []);

        const toggleFullscreen = useCallback(() => {
            const el = videoRef.current || containerRef.current;
            if (!el) return;
            if (document.fullscreenElement) document.exitFullscreen();
            else el.requestFullscreen?.();
        }, []);

        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`mx-auto px-4 sm:px-6 lg:px-8 ${s.maxWidth} ${anim}`}>
                    {(content.tag || content.title) && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div ref={containerRef} className="relative rounded-2xl overflow-hidden shadow-xl bg-gray-900" style={{ aspectRatio: '16/9' }}>
                        {isFile ? (
                            <>
                                <video ref={videoRef} src={video.src} className="w-full h-full object-cover" muted loop playsInline preload="metadata" />
                                <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                                    <button onClick={toggleMute}
                                        className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors backdrop-blur-sm">
                                        {isMuted ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/></svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/></svg>
                                        )}
                                    </button>
                                    <button onClick={toggleFullscreen}
                                        className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors backdrop-blur-sm">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/></svg>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {showThumb ? (
                                    <div className="w-full h-full cursor-pointer group" onClick={() => setShowThumb(false)}>
                                        {thumbnail && <img src={thumbnail} alt={content.title || 'Video'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                                            <div className="w-20 h-20 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110"
                                                style={{ backgroundColor: accent, boxShadow: `0 0 0 8px ${hexToRgba(accent, 0.15)}` }}>
                                                <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <iframe src={`${video.embed}?autoplay=1&rel=0&modestbranding=1&mute=1`}
                                        className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                                )}
                            </>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Simple (default) ──
    return (
        <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`mx-auto px-4 sm:px-6 lg:px-8 ${s.maxWidth} ${anim}`}>
                {(content.tag || content.title) && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <ThumbnailPlay />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            <VideoModal />
        </section>
    );
}
